import { LocationState } from 'history'
import { t } from 'i18next'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router'

import { ClinTheme } from '../../../../ClinTheme'
import { AnnounceMode } from '../../../../components/ClinAnnounceBar/ClinAnnounceBar'
import { ClinIcon } from '../../../../components/ClinIcon'
import { ClinIconPathName } from '../../../../components/ClinIcon/ClinIcon.paths'
import { useAppContext } from '../../../../context/app/AppContext'
import { createAnnounceEvent } from '../../../../events/AnnounceEvent'
import { AnalyticsEvent } from '../../../../services/Analytics/AnalyticsEvent'
import analyticsServiceSingleton from '../../../../services/Analytics/initAnalytics'
import {
  AuthError,
  getOrderById,
  upsertSkuItemToBasket,
  getPatientDetail,
  downloadOrderInvoice
} from '../../../../services/ApiService'
import { getVariantInformationForCatalogItem } from '../../../../services/PortfolioJourneys/PortfolioJourney.model'
import { OrderStatus } from '../../../../types/OrderStatus'
import { OrderDto, OrderSummaryDto } from '../../../../types/swaggerTypes'
import {
  OPAOrderTypes,
  PatientOrderStatus
} from '../../../Patients/PatientDashboard/Patient.model'
import TableActionMenu from '../../../Programs/ProgramDetail/TableActionMenu'
import { StyledActionWrapper } from '../Orders.styles'
import { isOrderEligibleForInvoice } from '../../../../utils/orderUtils'

// Create context to manage active dropdown state
const ActionDropdownContext = createContext<{
  activeDropdown: string | null
  setActiveDropdown: (orderId: string | null) => void
}>({
  activeDropdown: null,
  setActiveDropdown: () => {}
})

interface ActionDropdownItem {
  text: string
  color?: string
  onClick: (order: OrderSummaryDto) => void
  disabled?: boolean
  tooltip?: string
  isLoading?: boolean
}

interface ActionProps {
  order: OrderSummaryDto
  handleAddToBasketFromOrder?: (orderData: OrderDto) => void
  handleTrackOrder: () => void
}

// Provider component to wrap the table
export const ActionDropdownProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)

  return (
    <ActionDropdownContext.Provider
      value={{ activeDropdown, setActiveDropdown }}
    >
      {children}
    </ActionDropdownContext.Provider>
  )
}

const ActionsColumn: React.FC<ActionProps> = ({
  order,
  handleTrackOrder,
  handleAddToBasketFromOrder
}) => {
  const history = useHistory()
  const { activeDropdown, setActiveDropdown } = useContext(
    ActionDropdownContext
  )
  const [isAddingToBasket, setAddingToBasket] = React.useState<boolean>(false)

  // Check if this dropdown is currently active
  const isOpen = activeDropdown === order.orderNumber

  const { dispatch, defaultShippingAddress, portfolioCountryCode } =
    useAppContext()

  // const handleViewOrderDetails = (order: OrderSummaryDto) => {
  //   if (order.orderNumber) {
  //     history.push(`/order/${order.orderNumber}`, {
  //       from: window.location.pathname
  //     })
  //   }
  // }

  const handleOrderAgain = async (order: OrderSummaryDto) => {
    // Only attempt to get patient details if we have the IDs
    if (order.patientId && order.physicianId) {
      const patient = await getPatientDetail(
        order.patientId.toString(),
        order.physicianId.toString()
      )
      // One more step to go
      if (
        patient.data.patientOrderStatus ===
        PatientOrderStatus.IncompleteCheckout
      ) {
        history.push(`/opa-checkout/${order.orderNumber}`, {
          from: window.location.pathname,
          patientId: order.patientId,
          patientNumber: order.patientNumber,
          orderType: order.orderType,
          programId: order.programId,
          stockOrder: patient.data.stockOrderFlag
        })
        return
      }
    }

    if (order.programId) {
      handleMAOrder(order)
    } else {
      handleGAOrder(order)
    }
  }

  const handleMAOrder = (order: OrderSummaryDto) => {
    history.push(
      `/programs/access-programs/${order.programId}/patient-access-form/${order.physicianId}`,
      {
        orderType: OPAOrderTypes.Resupply,
        patientNumber: order.patientNumber,
        patientId: order.patientId,
        patientStatus: order.orderStatus,
        stockOrder: order.orderType,
        from: window.location.pathname
      } as LocationState
    )
  }

  const handleGetOrder = async () => {
    if (!order?.orderNumber) {
      return
    }

    setAddingToBasket && setAddingToBasket(true)

    getOrderById(order.orderNumber)
      .then((response) => {
        handleRequestAgain(response.data as OrderDto)
      })
      .catch((error) => {
        if (error.message !== AuthError.RequestCancelled) {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching the order. ${error}`
            )
          )
          let currentUser
          try {
            currentUser = JSON.parse(
              localStorage.getItem('current_user') ?? '{}'
            )
          } catch (e) {
            console.error('Error parsing current_user from localStorage', e)
          }
          analyticsServiceSingleton.trackError(error, currentUser)
          setAddingToBasket && setAddingToBasket(false)
        }
      })
  }

  const handleRequestAgain = async (orderData: OrderDto) => {
    if (!orderData || !defaultShippingAddress) {
      return
    }

    const StockKeepingUnits = orderData.lines
      .filter(
        (o) =>
          o.skuCatalogItem?.sku &&
          o.medicationFlag === 'Y' &&
          !getVariantInformationForCatalogItem(
            o.skuCatalogItem,
            portfolioCountryCode
          )?.cannotOrder
      )
      .map((o) => o.skuCatalogItem?.sku as string)

    if (StockKeepingUnits.length > 0) {
      try {
        await Promise.all(
          StockKeepingUnits.map((StockKeepingUnit) => {
            // Find the corresponding line item
            const lineItem = orderData.lines.find(
              (line) => line.skuCatalogItem?.sku === StockKeepingUnit
            )

            // Get quantity with safety checks
            let quantity = 1 // Default value
            if (
              lineItem?.quantities?.ordered &&
              typeof lineItem.quantities.ordered === 'number' &&
              lineItem.quantities.ordered > 0
            ) {
              quantity = lineItem.quantities.ordered
            }

            return upsertSkuItemToBasket(StockKeepingUnit, quantity)
          })
        )
        if (handleAddToBasketFromOrder) {
          handleAddToBasketFromOrder(orderData)
        }
        setAddingToBasket && setAddingToBasket(false)
      } catch (error) {
        dispatch(
          createAnnounceEvent(
            AnnounceMode.Error,
            `${t('order_detail:error_reordering')} ${error}`
          )
        )
        setAddingToBasket && setAddingToBasket(false)
      }
    }

    analyticsServiceSingleton.trackEvent(AnalyticsEvent.OpenRepeatOrderModal)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleGAOrder = (order: OrderSummaryDto) => {
    handleGetOrder()
  }

  const getStatusActions = (status: OrderStatus): ActionDropdownItem[] => {
    const actions: ActionDropdownItem[] = []

    // Check if this is a GA order (no programId means it's a GA order)
    const isGAOrder = !order.programId

    // const viewOrderDetailsAction = {
    //   text: 'orders:actions.view_order_details',
    //   onClick: () => handleViewOrderDetails(order)
    // }

    const orderAgainAction = {
      text: 'orders:actions.order_again',
      isLoading: isAddingToBasket,
      onClick: () => handleOrderAgain(order)
    }

    const trackOrderAction = {
      text: 'orders:actions.track_order',
      onClick: () => handleTrackOrder()
    }

    const downloadInvoiceAction = {
      text: 'orders:actions.download_invoice',
      onClick: () => handleDownloadInvoice(order),
      disabled: !isOrderEligibleForInvoice(order),
      tooltip: !isOrderEligibleForInvoice(order)
        ? 'orders:actions.invoice_not_available'
        : undefined
    }

    const isOrderAgainEnabled = order.orderabilityFlag === 'Y'

    // condition for MA orders
    const canOrderAgainMAOrder =
      order.programId &&
      order.physicianId &&
      order.patientNumber &&
      order.patientId &&
      order.orderStatus &&
      order.orderType === 'Resupply'

    // Status mapping according to the provided table
    switch (status) {
      case OrderStatus.Submitted:
        if (isGAOrder) {
          actions.push(orderAgainAction)
        }
        actions.push(downloadInvoiceAction)
        break

      case OrderStatus.Processing:
      case OrderStatus.Pending:
        if (isGAOrder) {
          actions.push(orderAgainAction)
        } else if (canOrderAgainMAOrder && isOrderAgainEnabled) {
          actions.push(orderAgainAction)
        }
        actions.push(downloadInvoiceAction)
        break

      case OrderStatus.Shipped:
      case OrderStatus.InTransit:
      case OrderStatus.In_Transit:
      case OrderStatus.OutForDelivery:
      case OrderStatus.Out_For_Delivery:
      case OrderStatus.FailedAttempt:
      case OrderStatus.Failed_Attempt:
      case OrderStatus.AvailableForPickup:
      case OrderStatus.Delivered:
      case OrderStatus.FailedDelivery:
      case OrderStatus.Failed_Delivery:
        if (isGAOrder) {
          actions.push(orderAgainAction)
        } else if (canOrderAgainMAOrder && isOrderAgainEnabled) {
          actions.push(orderAgainAction)
        }
        actions.push(trackOrderAction)
        actions.push(downloadInvoiceAction)
        break

      case OrderStatus.Cancelled:
      case OrderStatus.UnderMedicalReview:
      case OrderStatus.Under_Medical_Review:
      case OrderStatus.HealthAuthorityReview:
      case OrderStatus.Health_Authority_Review:
      case OrderStatus.MedicalReviewDeclined:
      case OrderStatus.Medical_Review_Declined:
      case OrderStatus.HealthAuthorityDeclined:
      case OrderStatus.Health_Authority_Declined:
      case OrderStatus.On_Hold:
      case OrderStatus.OnHold:
      case OrderStatus.Under_Review:
        if (isGAOrder) {
          actions.push(orderAgainAction)
        }
        actions.push(downloadInvoiceAction)
        break
    }

    return actions
  }

  const handleDownloadInvoice = async (order: OrderSummaryDto) => {
    if (!isOrderEligibleForInvoice(order)) {
      dispatch(
        createAnnounceEvent(
          AnnounceMode.Warning,
          t('order_detail:error_sending_invoice')
        )
      )
      return
    }

    try {
      dispatch(
        createAnnounceEvent(
          AnnounceMode.Information,
          t('order_detail:invoice_download_started')
        )
      )

      const response = await downloadOrderInvoice(order.orderNumber)

      if (response.status === 200) {
        // Create a URL for the blob
        const blob = response.data
        const downloadUrl = window.URL.createObjectURL(blob)

        // Create a temporary link and click it to download
        const a = document.createElement('a')
        a.href = downloadUrl
        a.download = `invoice_${order.orderNumber}.pdf`
        document.body.appendChild(a)
        a.click()

        // Clean up
        window.URL.revokeObjectURL(downloadUrl)
        document.body.removeChild(a)

        dispatch(
          createAnnounceEvent(
            AnnounceMode.Information,
            t('order_detail:invoice_downloaded')
          )
        )

        analyticsServiceSingleton.trackEvent(AnalyticsEvent.DownloadInvoice, {
          orderNumber: order.orderNumber,
          orderStatus: order.orderStatus,
          orderType: order.orderType,
          patientId: order.patientId,
          patientNumber: order.patientNumber,
          physicianId: order.physicianId
        })
      }
    } catch (error) {
      dispatch(
        createAnnounceEvent(
          AnnounceMode.Error,
          `${t('order_detail:error_downloading_invoice')} ${error}`
        )
      )

      analyticsServiceSingleton.trackEvent(
        AnalyticsEvent.DownloadInvoiceError,
        {
          orderNumber: order.orderNumber,
          orderStatus: order.orderStatus,
          orderType: order.orderType,
          patientId: order.patientId,
          patientNumber: order.patientNumber,
          physicianId: order.physicianId
        }
      )
    }
  }

  const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    // If this dropdown is already open, close it
    // Otherwise, set this dropdown as active (which will close any other open dropdown)
    setActiveDropdown(isOpen ? null : order.orderNumber || null)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Trigger action on Enter or Space key
    if (e.key === 'Enter' || e.key === ' ') {
      handleToggle(e)
    }
  }

  const handleCloseActionMenu = () => {
    setActiveDropdown(null)
  }

  const [actions, setActions] = useState<ActionDropdownItem[]>(() =>
    getStatusActions(order.orderStatus as OrderStatus)
  )

  useEffect(() => {
    setActions(getStatusActions(order.orderStatus as OrderStatus))
  }, [isAddingToBasket, order])

  return (
    <StyledActionWrapper>
      <button
        onClick={actions.length > 0 ? handleToggle : undefined}
        onKeyDown={actions.length > 0 ? handleKeyDown : undefined}
        disabled={actions.length === 0}
        aria-label="Open actions menu"
        aria-haspopup="true"
        aria-expanded={isOpen}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: actions.length > 0 ? 'pointer' : 'default'
        }}
      >
        <ClinIcon
          iconName={ClinIconPathName.ThreeDots}
          iconFill={
            actions.length > 0
              ? ClinTheme.colors.darkGrey
              : ClinTheme.colors.grey
          }
          iconSize={28}
          viewBox="0 0 25 26"
        />
      </button>
      {isOpen && actions.length > 0 && (
        <TableActionMenu
          items={actions}
          action={order}
          handleCloseActionMenu={handleCloseActionMenu}
        />
      )}
    </StyledActionWrapper>
  )
}

export default ActionsColumn
